import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DeviceService } from 'shared/services/device.service';

import { DeviceModel } from '../../definition';

@Component({
    selector: 'app-device-selector',
    templateUrl: './device-selector.component.html',
    styleUrls: ['./device-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DeviceSelectorComponent),
            multi: true
        }
    ]
})
export class DeviceSelectorComponent implements OnInit, ControlValueAccessor, OnChanges {
    /**
     * 启用设备过滤NeedChangeManually为true的选项
     * @type {boolean}
     */
    @Input() enabledFilterNeedChangeManually = false;
    /**
     * 自动加载第一个选项作为默认值
     * @type {boolean}
     */
    @Input() autoLoadFirstOption = false;
    /**
     * 最多显示多少个 tag
     * @type {number}
     */
    @Input() maxTagCount = 1;
    /**
     * 模式选择
     * @type {"multiple" | "tags" | "default"}
     */
    @Input() mode: 'multiple' | 'tags' | 'default' = 'multiple';
    /**
     * 占位符
     * @type {string}
     */
    @Input() placeholder: string;
    /**
     * 设备ID，一个或多个
     * @type {string | string[]}
     */
    @Input() value: string | string[];
    /**
     * 层级路径
     * @type {string}
     */
    @Input() hierarchicalFullName: string;
    /**
     * 允许清空
     * @type {boolean}
     */
    @Input() allowClear = true;
    /**
     * 允许搜索
     *
     * @type {boolean}
     */
    @Input() showSearch = true;
    /**
     * 全部设备列表
     * @type {DeviceModel[]}
     */
    devices: DeviceModel[];

    /**
     * 选择更新
     * @type {EventEmitter<string | string[]>}
     */
    @Output() change = new EventEmitter<string | string[]>();
    /**
     * 初始化
     * @type {EventEmitter<string | string[]>}
     */
    @Output() init = new EventEmitter<string | string[]>();
    @Output() changeDevice = new EventEmitter<DeviceModel | DeviceModel[]>();
    options: DeviceModel[];
    @Input() disabled: boolean;

    constructor(private deviceService: DeviceService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        const { enabledFilterNeedChangeManually, hierarchicalFullName } = changes;
        if (enabledFilterNeedChangeManually || hierarchicalFullName) {
            this.updateOptions();
        }
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    ngOnInit() {
        this.deviceService.getList().subscribe(devices => {
            this.devices = devices;
            this.updateOptions();
            this.init.emit(this.value);
        });
    }

    propagateOnChange: (value: any) => void = (_: any) => {
    };

    propagateOnTouched: (value: any) => void = (_: any) => {
    };

    registerOnChange(fn: any): void {
        this.propagateOnChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.propagateOnTouched = fn;
    }

    writeValue(obj: any): void {
        this.value = obj || this.getEmptyValue();
        this.change.emit(obj);
        this.changeDevice.emit(this.getSelection());
        this.propagateOnChange(obj);
    }

    getValue(device: DeviceModel) {
        return device?.fullName || '';
    }

    private getSelection() {
        if (this.mode === 'multiple') {
            return this.getMultipleSelection();
        } else {
            return this.getSingleSelection();
        }
    }

    private getSingleSelection() {
        return this.devices?.find(device => this.getValue(device) === this.value) || null;
    }

    private getMultipleSelection() {
        return this.devices?.filter(device => this.value?.includes(this.getValue(device) || '')) || [];
    }

    private updateOptions() {
        const options = this.devices?.filter(device => this.filter(device)) || [];
        this.options = options;
        if (this.autoLoadFirstOption && options?.length > 0) {
            if (this.mode === 'multiple') {
                this.writeValue(options.length > 0 ? options.slice(0, 1).map(device => this.getValue(device)) : null);
            } else {
                this.writeValue(options.length > 0 ? this.getValue(options[0]) : null);
            }
        }
    }

    private filter(device: DeviceModel) {
        return this.hierarchicalFullName?.length > 0
            ? !!device
            && (!this.enabledFilterNeedChangeManually || device.needChangeManually) && (
                typeof this.hierarchicalFullName === 'string' && device?.fullName?.startsWith(this.hierarchicalFullName) ||
                typeof this.hierarchicalFullName === 'object' && !!((this.hierarchicalFullName || []) as string[])?.find(fullName =>
                    device?.fullName?.startsWith(fullName))
            ) : [];
    }

    private getEmptyValue() {
        return this.mode === 'multiple' ? [] : null;
    }
}
